import React, { useEffect, useState } from 'react';
import { getCollectionRarity } from '@/lib/api';

const TIER_COLORS: Record<string, string> = {
  bronze: '#cd7f32',
  silver: '#c0c0c0',
  gold: '#ffd700',
  platinum: '#e5e4e2',
  diamond: '#b9f2ff',
};

// Trait rarity breakdown for a collection, shown in the sidebar filter panel.
// Fetches pre-aggregated rarity stats from POST /api/v1/collectionRarity and
// renders the rarest trait combinations with their rarity tier badge
// (bronze→diamond). Each row shows the trait value + a tier badge; hover shows
// the trait type and how many tokens carry it. Renders nothing when the
// collection has no rarity backfill (empty traits array).
const TraitRarityStats = ({ collId }: { collId?: string | number }) => {
  const [rarity, setRarity] = useState<any>(null);

  useEffect(() => {
    if (collId === undefined || collId === null || collId === '') return;
    let cancelled = false;
    getCollectionRarity(collId)
      .then((data) => {
        if (!cancelled) setRarity(data);
      })
      .catch(() => {
        if (!cancelled) setRarity(null);
      });
    return () => {
      cancelled = true;
    };
  }, [collId]);

  const traits =
    rarity?.traits && Array.isArray(rarity.traits) && rarity.traits.length > 0
      ? rarity.traits
      : null;

  if (!traits) return null;

  // Top 8 rarest trait values (rarity_score is already descending from API).
  const top = traits.slice(0, 8);

  return (
    <div>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: '#20ff43',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          marginBottom: 8,
          borderBottom: '1px solid #16325c',
          paddingBottom: 4,
        }}
      >
        Trait Rarity
      </div>
      <div style={{ fontSize: 11, color: '#c8d2e6', lineHeight: 1.8 }}>
        {top.map((t) => {
          const tier = TIER_COLORS[t.tier] ? t.tier : 'bronze';
          const label = t.trait_value || String(t.trait_type ?? '').trim() || '—';
          return (
            <div
              key={`${t.trait_type}::${t.trait_value}`}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 8,
                padding: '2px 0',
                borderBottom: '1px solid rgba(22,50,92,0.4)',
              }}
              title={`${t.trait_type ?? 'Trait'}: ${label} — appears on ${t.token_count ?? '?'} token${t.token_count === 1 ? '' : 's'}`}
            >
              <span style={{ wordBreak: 'break-word', flex: 1 }}>{label}</span>
              <span
                style={{
                  color: '#0a1428',
                  background: TIER_COLORS[tier],
                  borderRadius: 3,
                  padding: '1px 6px',
                  fontSize: 9,
                  fontWeight: 800,
                  letterSpacing: '0.5px',
                  whiteSpace: 'nowrap',
                }}
              >
                {tier.toUpperCase()}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TraitRarityStats;